import * as React from 'react';
import { useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { IOption, IOptionGroup } from 'common/ui/interfaces/selectors';
import { Divider } from '@mui/material';
import {
  DocumentTypeEnum,
  GroupDocumentTypeEnum,
} from '../../../../common/document/enums/document-type.enum';
import { ChipStyled } from '../../../../components/Form/Chip';
import { LabelStyled } from 'components/Form/Labels';
import { ReactComponent as SelectIcon } from 'assets/images/arrow-select.svg';
import { InputSelect } from '../../../../components/Form/InputSelect';
import { ListSubheaderStyled } from '../../../../components/Form/ListSubheader';
import { MenuItemMultipleSelect } from '../../../../components/Form/MenuItemMultipleSelect';
import { ListItemTextStyled } from '../../../../components/Form/ListItemText';
import {
  CDL,
  DOTMedCert,
  DriverChecklist,
  MedicalDisontinuance,
  Onboarding,
  PhotoIdDocument,
  ReturnToWork,
} from '../../../../common/document/ui/documents';

import styles from './styles.module.scss';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 6.5 + ITEM_PADDING_TOP,
      width: 250,
      paddingTop: '5px',
    },
  },
};

const documents: IOptionGroup[] = [
  {
    groupLabel: 'Document Groups',
    options: [Onboarding],
  },
  {
    groupLabel: 'Individual Documents',
    options: [
      CDL,
      DOTMedCert,
      DriverChecklist,
      ReturnToWork,
      MedicalDisontinuance,
      PhotoIdDocument,
    ],
  },
];

type InputProps = {
  label: string;
  required?: boolean;
  error?: boolean;
  onChange: (option: IOption[]) => void;
  selected: IOption[];
};

export default function MultipleSelectChipDocuments({
  label,
  error,
  onChange,
  selected,
}: InputProps) {
  const onboardingItems = [
    DocumentTypeEnum.DRIVER_LICENSE,
    DocumentTypeEnum.DOT_MEDICAL,
    DocumentTypeEnum.DRIVER_CHECKLIST,
  ];

  const isDisabled = useCallback(
    (value: any) => {
      if (value === GroupDocumentTypeEnum.ONBOARDING) {
        return false;
      }
      return (
        selected
          .map((option) => option.value)
          .includes(GroupDocumentTypeEnum.ONBOARDING) &&
        onboardingItems.includes(value)
      );
    },
    [selected]
  );

  const isChecked = useCallback(
    (value: any) => {
      if (
        selected
          .map((option) => option.value)
          .includes(GroupDocumentTypeEnum.ONBOARDING) &&
        onboardingItems.includes(value)
      ) {
        return true;
      }
      return selected.map((option) => option.value).includes(value);
    },
    [selected]
  );

  const handleClick = (option: IOption) => {
    let newValue = [];
    if (
      (!isChecked(option.value) &&
        selected.length &&
        !selected
          .map((option) => option.value)
          .includes(GroupDocumentTypeEnum.ONBOARDING) &&
        onboardingItems.every(
          (value) =>
            option.value === value ||
            selected.map((op) => op.value).includes(value)
        )) ||
      (!isChecked(option.value) &&
        option.value === GroupDocumentTypeEnum.ONBOARDING)
    ) {
      newValue = [
        Onboarding,
        ...selected.filter(
          (op) => !onboardingItems.includes(op.value as DocumentTypeEnum)
        ),
      ];
    } else {
      newValue = isChecked(option.value)
        ? [...selected.filter((op) => op.value !== option.value)]
        : [...selected, option];
    }
    onChange(newValue);
  };

  const heightControlStyle = useMemo(() => {
    if (selected.length > 1) {
      return { height: 'auto' };
    }
    return {};
  }, [selected]);

  return (
    <FormControl sx={{ m: 0, p: 0, width: '100%' }} style={{ height: '100%' }}>
      <InputLabel
        id="multiple-chip-label"
        color="secondary"
        classes={{
          root: styles.label,
          focused: styles.labelFocused,
          shrink: styles.labelFocused,
        }}
      >
        <LabelStyled required={true}>{label}</LabelStyled>
      </InputLabel>
      <Select
        labelId="multiple-chip-label"
        id="multiple-chip"
        style={heightControlStyle}
        multiple
        value={selected}
        IconComponent={SelectIcon}
        input={
          <InputSelect id="select-multiple-chip" label={label} error={error} />
        }
        renderValue={(selected) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selected.map((option) => (
              <ChipStyled key={option.value} label={option.label} />
            ))}
          </Box>
        )}
        MenuProps={MenuProps}
      >
        {documents.map((group, index) => (
          <div key={group.groupLabel} style={{ height: 'auto' }}>
            {!!index && (
              <Divider
                sx={{
                  margin: '5px 15px 14px',
                }}
              />
            )}
            <ListSubheaderStyled>{group.groupLabel}</ListSubheaderStyled>
            {group.options.map((option) => (
              <MenuItemMultipleSelect
                key={option.value}
                value={option.value}
                selected={isChecked(option.value)}
                disabled={isDisabled(option.value)}
                onClick={() => {
                  handleClick(option);
                }}
                sx={{
                  '&.Mui-selected, &.Mui-selected:hover': {
                    backgroundColor: '#BEFFEF',
                  },
                }}
              >
                <ListItemTextStyled
                  primary={option.label}
                  style={{ padding: '10px' }}
                />
              </MenuItemMultipleSelect>
            ))}
          </div>
        ))}
      </Select>
    </FormControl>
  );
}
